import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faFileLines,
  faArrowRight,
} from '@fortawesome/pro-light-svg-icons';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { TrueImpactButtonWithLoading } from 'shared/design-system/components/TrueImpactButton';
import TrueImpactConfirmationDialog from 'shared/design-system/components/TrueImpactConfirmationDialog';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { updateReportReportingType } from '../../ReportBuilder/Components/Review/api';
import { getProgramNameUrl } from '../../ReportBuilder/Components/Budget/utils';
import DoveAnimation from './DoveAnimation';
import { pullReportData, useTrackEvent } from 'shared/utils/useTrackEvent';
import { useReport } from 'shared/providers/ReportProvider';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import {
  FeedbackStatus,
  ReportStage,
} from 'donor/portal/pages/FeedbackDashboard/api';

const SHOW_DOVE_ANIMATION = false;

const pop = keyframes`
  0% { transform: scale(0.6); opacity: 0; }
  60% { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.35; }
  100% { transform: scale(1.6); opacity: 0; }
`;

const StyledDiv = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACERS.medium};
  padding: ${SPACERS.extraLarge} ${SPACERS.large};
  flex: 1 1 auto;
  height: 100%;
  min-height: 100%;
  align-self: stretch;
  text-align: center;
  overflow: hidden;
  background: linear-gradient(
    180deg,
    ${COLORS.ctaPrimaryBackground} 0%,
    ${COLORS.whiteBase} 60%
  );

  .icon-wrapper {
    position: relative;
    width: 96px;
    height: 96px;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: ${pop} 500ms ease-out both;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background-color: ${COLORS.ctaPrimary};
      animation: ${pulse} 1.6s ease-out infinite;
    }

    .check-icon {
      position: relative;
      color: ${COLORS.ctaPrimary};
      font-size: 96px;
    }
  }

  .title {
    margin-top: ${SPACERS.small};
  }

  .subtitle {
    max-width: 480px;
  }

  .actions {
    margin-top: ${SPACERS.medium};
    display: flex;
    gap: ${SPACERS.small};
    button {
      min-width: 220px;
    }
  }
  .upgrade-section {
    width: 100%;
    max-width: 800px;
    margin-top: ${SPACERS.large};
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${SPACERS.tiny};
  }
  .upgrade-kicker {
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .upgrade-card {
    width: 100%;
    display: flex;
    align-items: center;
    gap: ${SPACERS.medium};
    padding: ${SPACERS.medium} ${SPACERS.large};
    border: 1px solid ${COLORS.greyBase};
    border-radius: 8px;
    background-color: ${COLORS.whiteBase};
    text-align: left;
    .upgrade-icon {
      font-size: 28px;
      color: ${COLORS.greyDark};
      flex-shrink: 0;
    }
    .upgrade-copy {
      flex: 1;
    }
    button {
      flex-shrink: 0;
    }
  }
`;

interface PublishedScreenProps {
  onClose: () => void;
  programName?: string;
  reportId?: number | string;
  previousStatus?: FeedbackStatus | null;
  previousStage?: ReportStage | null;
}

const PublishedScreen: React.FC<PublishedScreenProps> = ({
  onClose,
  programName,
  reportId,
  previousStatus,
  previousStage,
}) => {
  const client = useClient();
  const trackEvent = useTrackEvent();
  const { userStaticType } = useUserActions();
  const { report: reportState, isReportEndDatePassed } = useReport();
  const { push } = useHistory();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [upgradeState, makeUpgradeRequest] = useGraphQLRequest();

  const isUpgrading = upgradeState.type === 'REQUEST_START';
  const isUpgraded = upgradeState.type === 'REQUEST_SUCCESS';
  const upgradeFailed = upgradeState.type === 'REQUEST_ERROR';

  const openConfirm = useCallback(() => setConfirmOpen(true), []);
  const closeConfirm = useCallback(() => {
    if (!isUpgrading) setConfirmOpen(false);
  }, [isUpgrading]);

  const handleUpgrade = useCallback(() => {
    if (!reportId || !client) return;
    const request = updateReportReportingType(
      client,
      reportId,
      'FULL',
      'Draft',
      undefined,
      true,
    );
    request.then(() => {
      const reportData =
        reportState.type === 'REQUEST_SUCCESS' ? reportState.data : undefined;
      trackEvent('ReportStatusChanged', {
        ...(reportData
          ? pullReportData(reportData, isReportEndDatePassed, userStaticType)
          : {}),
        reportId,
        previousStatus,
        previousStage,
        status: 'Draft',
        stage: previousStage,
      });
    });
    makeUpgradeRequest(request);
  }, [client, reportId, makeUpgradeRequest]);

  useEffect(() => {
    if (!isUpgraded || !reportId) return;
    setConfirmOpen(false);
    push(`/npo/report-builder/${getProgramNameUrl(programName)}/${reportId}`);
  }, [isUpgraded, reportId, programName, push]);

  return (
    <StyledDiv>
      {SHOW_DOVE_ANIMATION && <DoveAnimation />}
      <div className="icon-wrapper">
        <FontAwesomeIcon icon={faCircleCheck} className="check-icon" />
      </div>
      <TrueImpactText variant="heading-page" className="title">
        Impact Snapshot published
      </TrueImpactText>
      <TrueImpactText variant="body" color="greyDark" className="subtitle">
        {programName ? (
          <>
            Your impact snapshot for <strong>{programName}</strong>
            <br />
            is now published.
          </>
        ) : (
          <>Your impact snapshot is now published.</>
        )}
      </TrueImpactText>
      <div className="actions">
        <TrueImpactButtonWithLoading
          variant="primary-large"
          isLoading={false}
          onClick={onClose}
        >
          Done
        </TrueImpactButtonWithLoading>
      </div>
      {reportId && !isUpgraded && (
        <div className="upgrade-section">
          <TrueImpactText
            variant="body-small"
            color="greyDark"
            className="upgrade-kicker"
          >
            Optional next step
          </TrueImpactText>
          <div className="upgrade-card">
            <FontAwesomeIcon icon={faFileLines} className="upgrade-icon" />
            <TrueImpactText
              variant="body"
              color="greyDark"
              className="upgrade-copy"
            >
              Upgrade to a full report to complete your full impact model and
              add narratives. All your snapshot data carries over.
            </TrueImpactText>
            <TrueImpactButtonWithLoading
              variant="secondary-large"
              isLoading={false}
              onClick={openConfirm}
            >
              Upgrade to full report <FontAwesomeIcon icon={faArrowRight} />
            </TrueImpactButtonWithLoading>
          </div>
        </div>
      )}

      <TrueImpactConfirmationDialog
        open={confirmOpen}
        icon={faFileLines}
        handleClose={closeConfirm}
        next={{
          isLoading: isUpgrading,
          text: isUpgrading ? 'Upgrading...' : 'Upgrade to full report',
          onClick: handleUpgrade,
        }}
        previous={{
          text: 'Cancel',
          onClick: closeConfirm,
        }}
      >
        <TrueImpactText variant="heading" align="center">
          Upgrade to a full report?
        </TrueImpactText>
        <TrueImpactText align="center" color="greyDark">
          This will convert{' '}
          {programName ? <strong>{programName}</strong> : 'this report'} into a
          full report. All your snapshot data carries over, and you&rsquo;ll be
          able to complete your full impact model and add narratives. This
          cannot be undone.
        </TrueImpactText>
        {upgradeFailed && (
          <TrueImpactText align="center" color="critical">
            Something went wrong upgrading the report. Please try again.
          </TrueImpactText>
        )}
      </TrueImpactConfirmationDialog>
    </StyledDiv>
  );
};

export default PublishedScreen;
